import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MeterDetails } from '../../models/registration.models';
import { RegisterService, RegistrationDetails } from '../../services/register.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CommonModule } from '@angular/common';
import { UtilService } from '../../services/util.service';
import { Location } from '@angular/common';
import { ConsentWalletFcComponent } from '../../components/consent-wallet-fc.component';
import { CustomerDetailsComponent } from '../../components/customer-details/customer-details.component';
import { MeterSelectionComponent } from '../../components/meter-selection/meter-selection.component';
import { LoginService } from '../../services/login.service';

@Component({
  selector: 'app-registration',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  standalone: true,
  imports: [
    MatStepperModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    ReactiveFormsModule,
    MatProgressBarModule,
    CommonModule,
    ConsentWalletFcComponent,
    CustomerDetailsComponent,
    MeterSelectionComponent
  ],
})
export class RegisterComponent implements OnInit {

  // View child for stepper
  @ViewChild('stepper') stepper!: MatStepper;
  
  registrationForm: FormGroup;
  registrationInProgress = false;

  @Input() onboardMeter = false;
  @Input() tpi = false;
  @Input() email?: string;

  constructor(
    private fb: FormBuilder, 
    private loginService: LoginService, 
    private regService: RegisterService, 
    private util: UtilService, 
    private location: Location
  ) {
    this.registrationForm = this.fb.group({
      // Step 1: Customer Details (combines registration type + personal/company details)
      customerDetails: [null], // Validation handled by CustomerDetailsComponent
      
      // Step 2: Meter Selection
      meterSelection: [null, Validators.required],
      
      // Step 3: Consent
      consent: [false, Validators.requiredTrue],
      
      // Step 4: Terms and conditions
      agreeToTerms: [false, Validators.requiredTrue],
    });
  }

  ngOnInit() {
    // If onboarding a meter with email, pre-populate email
    if (!this.tpi && this.onboardMeter && this.email) {
      // We'll set this after the customer details component is initialized
      setTimeout(() => {
        const defaultCustomerDetails = {
          registeringAs: 'individual',
          personalDetails: {
            firstName: '',
            lastName: '',
            email: this.email,
            phone: ''
          },
          companyDetails: {
            companyNumber: '',
            companyName: ''
          }
        };
        this.registrationForm.get('customerDetails')?.setValue(defaultCustomerDetails);
      });
    }

    // Handle consent requirement based on meter selection
    this.registrationForm.get('meterSelection')?.valueChanges.subscribe(val => {
      if (val instanceof File) {
        // For file uploads, consent is not required (emails will be sent)
        this.registrationForm.get('consent')?.clearValidators();
        this.registrationForm.get('consent')?.setValue(true);
      } else {
        // For individual meter selections, consent is required
        this.registrationForm.get('consent')?.setValidators([Validators.requiredTrue]);
        this.registrationForm.get('consent')?.setValue(false);
      }
      this.registrationForm.get('consent')?.updateValueAndValidity();
    });
  }

  isConsentRequired(): boolean {
    const meterSelection = this.registrationForm.get('meterSelection')?.value;
    return !(meterSelection instanceof File);
  }

  isCustomerDetailsValid(): boolean {
    return this.registrationForm.get('customerDetails')?.valid || false;
  }

  isMeterSelectionValid(): boolean {
    const meterSelection = this.registrationForm.get('meterSelection')?.value;
    return (Array.isArray(meterSelection) && meterSelection.length > 0) || 
           (meterSelection instanceof File);
  }

  isConsentValid(): boolean {
    return this.registrationForm.get('consent')?.valid || false;
  }

  allStepsValid(): boolean {
    return this.registrationForm.valid;
  }

  getCustomerDetailsForConsent() {
    const customerDetails = this.registrationForm.get('customerDetails')?.value;
    if (!customerDetails) return undefined;
    
    const personalDetails = customerDetails.personalDetails;
    const isCompany = customerDetails.registeringAs === 'company';
    
    let details: any = {
      email: personalDetails?.email,
      firstName: personalDetails?.firstName,
      lastName: personalDetails?.lastName,
      phoneNumber: personalDetails?.phone,
      signUpType: isCompany ? 'company' : 'individual'
    };

    if (isCompany) {
      const companyDetails = customerDetails.companyDetails;
      details.companyDetails = {
        companyNumber: companyDetails?.companyNumber,
        companyName: companyDetails?.companyName
      };
    }

    return details;
  }

  getMeterDetailsForConsent() {
    const meterSelection = this.registrationForm.get('meterSelection')?.value;
    
    if (!meterSelection) {
      return undefined;
    }

    // Handle file upload case
    if (meterSelection instanceof File) {
      return {
        meters: [{
          mpxn: `File: ${meterSelection.name}`,
          fuelType: 'Elec' as const,
          address: `File size: ${(meterSelection.size / 1024).toFixed(2)} KB`
        }]
      };
    }

    // Handle meter array case
    if (Array.isArray(meterSelection) && meterSelection.length > 0) {
      return {
        meters: meterSelection.map((meter: MeterDetails) => ({
          mpxn: meter.mpanCore,
          fuelType: 'Elec' as const,
          address: meter.addressAsLine || meter.addressId
        }))
      };
    }

    return undefined;
  }

  private isSmartMeter(meterDetails: MeterDetails): boolean {
    return meterDetails?.meterType?.toLowerCase().startsWith('s');
  }

  onSubmit() {
    if (!this.allStepsValid()) {
      return;
    }

    this.registrationInProgress = true;

    const formValue = this.registrationForm.getRawValue();
    const customerDetails = formValue.customerDetails;
    const personalDetails = customerDetails?.personalDetails;
    const meterSelection = formValue.meterSelection;

    let registrationDetails: RegistrationDetails = {
      firstname: personalDetails?.firstName,
      lastname: personalDetails?.lastName,
      email: personalDetails?.email,
      phone: personalDetails?.phone,
      signature: `${personalDetails?.firstName} ${personalDetails?.lastName}`,
      terms: formValue.agreeToTerms
    };

    if (customerDetails?.registeringAs === 'company') {
      const companyDetails = customerDetails.companyDetails;
      registrationDetails = {
        ...registrationDetails,
        orgId: companyDetails?.companyNumber,
        org: companyDetails?.companyName?.name || companyDetails?.companyName
      };
    }

    // Handle meter details
    if (meterSelection) {
      if (meterSelection instanceof File) {
        // Handle file upload case
        registrationDetails = {
          ...registrationDetails,
          mpanCount: 'multiple',
          multimeterFile: meterSelection
        };
      } else if (Array.isArray(meterSelection) && meterSelection.length > 0) {
        // Handle meter array case
        if (meterSelection.length === 1) {
          registrationDetails = {
            ...registrationDetails,
            mpan: meterSelection[0].mpanCore,
            meterType: this.isSmartMeter(meterSelection[0]) ? 'smart' : 'hh'
          };
        } else {
          registrationDetails = {
            ...registrationDetails,
            mpanCount: 'multiple'
          };
        }
      }
    }

    console.log('Registration Details:', registrationDetails);

    this.regService.registerUser(registrationDetails).subscribe({
      next: (response) => {
        console.log('Registration successful:', response);
        this.loginService.sendMagicLink(personalDetails.email).subscribe();
        this.stepper.selectedIndex = 0; 
        this.registrationInProgress = false;
        this.registrationForm.reset();
        
        if (this.email) {
          this.location.back();
        } else {
          this.util.openSnackBar(
            'Your registration was successful. Please keep an eye on your email inbox, and your secure magic link will be delivered shortly, allowing you to manage your account with ease.', 
            'Close', 
            'success'
          );
        }
      },
      error: (error) => {
        console.error('Error registering:', error);
        this.registrationInProgress = false;
        
        if (error?.error?.message?.[0]?.message) {
          if (error.error.message[0].message === 'Meter Already Onboarded') {
            this.stepper.selectedIndex = 1; 
          }
          this.util.openSnackBar(error.error.message[0].message, 'Close', 'error');
        } else {
          this.util.openSnackBar('Something went wrong. Please try again', 'Close', 'error');
        }
      }
    });
  }
}
